import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import { createEvent } from '../services/eventService';
import { Tag, Users, ChevronLeft, ChevronRight, Check, Info, X, Sparkles, MapPin, Wand2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import LocationSearch from './LocationSearch';
import InterestSelector from './InterestSelector';
import DatePicker from './DatePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { AVAILABLE_INTERESTS } from '../constants/interests';
import VybeCreatedModal from './VybeCreatedModal';
import TokenTopUpModal from './TokenTopUpModal';
import { canCreateVybe, consumeVybeCreateLimitWithRefresh } from '../utils/vybeLimits';
import { containsProfanity } from '../utils/contentFilter';
import { trackEvent } from '../services/analyticsService';
import { trackVybeCreated as trackVybeCreatedB2B } from '../services/trackingService';
import {
  trackVybeCreated,
  trackCreateVybeStepStarted,
  trackCreateVybeStepCompleted,
  trackCreateVybePublishClicked,
  standardizeCategory,
} from '../utils/ga4';

interface EventFormData {
  title: string;
  description: string;
  location: string;
  date: string;
  category: string;
  maxApplicants: string;
  preferredGender: 'male' | 'female' | 'prefer-not-to-say' | 'non-binary';
  minAge: string;
  maxAge: string;
  preferredInterests: string[];
  latitude?: number;
  longitude?: number;
  imageUrl?: string;
  visibility: 'public' | 'tribe_only';
  tone: string;
  customImage?: File | null;
  customImageUrl?: string;
  isCommunityVybe?: boolean;
}

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated: () => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ isOpen, onClose, onEventCreated }) => {
  const { currentUser, refreshCurrentUser } = useAuth();
  useOverlayState(isOpen, 'create-event-modal');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    location: '',
    date: '',
    category: '',
    maxApplicants: '10',
    preferredGender: 'prefer-not-to-say',
    minAge: '',
    maxAge: '',
    preferredInterests: [],
    visibility: 'public',
    tone: 'friendly',
    customImage: null,
    customImageUrl: '',
    isCommunityVybe: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [createdEventId, setCreatedEventId] = useState<string>('');
  const [showVybeCreated, setShowVybeCreated] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState<string>('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string>('');

  // Only reset form when modal is opened and was previously successful, or when closing
  useEffect(() => {
    if (!isOpen && success) {
      // Reset on close after success
      setStep(1);
      setFormData({
        title: '',
        description: '',
        location: '',
        date: '',
        category: '',
        maxApplicants: '10',
        preferredGender: 'prefer-not-to-say',
        minAge: '',
        maxAge: '',
        preferredInterests: [],
        visibility: 'public',
        tone: 'friendly',
        customImage: null,
        customImageUrl: ''
      });
      setErrors({});
      setShowCancelConfirm(false);
      setSuccess(false);
    }
  }, [isOpen, success]);

  // Prevent form submission when pressing Enter in input fields on steps 1 and 2
  // BUT allow Enter in textareas (for line breaks in description)
  useEffect(() => {
    const preventEnterSubmit = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT';
      const isTextarea = target.tagName === 'TEXTAREA';

      // Prevent Enter on input fields but allow it on textareas
      if (e.key === 'Enter' && isInput && step < 3) {
        e.preventDefault();
      }
      // Allow Enter in textareas - do nothing
    };
    window.addEventListener('keydown', preventEnterSubmit);
    return () => window.removeEventListener('keydown', preventEnterSubmit);
  }, [step]);

  const categories = [...AVAILABLE_INTERESTS];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleInterestChange = (interests: string[]) => {
    setFormData(prev => ({ ...prev, preferredInterests: interests }));
    if (errors.preferredInterests) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.preferredInterests;
        return newErrors;
      });
    }
  };

  const handleLocationSelect = (location: { name: string; latitude: number; longitude: number }) => {
    setFormData(prev => ({
      ...prev,
      location: location.name,
      latitude: location.latitude,
      longitude: location.longitude
    }));
    if (errors.location) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({ ...prev, date }));
    if (errors.date) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.date;
        return newErrors;
      });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, customImage: 'Please select a valid image file' }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, customImage: 'Image must be less than 5MB' }));
      return;
    }

    setFormData(prev => ({ ...prev, customImage: file }));

    // Clear any previous errors
    if (errors.customImage) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.customImage;
        return newErrors;
      });
    }
  };

  const handleEnhanceWithAI = async () => {
    if (!formData.title.trim()) {
      setDescriptionError('Please add a title first');
      return;
    }

    setIsGeneratingDescription(true);
    setDescriptionError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-vybe-description`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            title: formData.title,
            raw_description: formData.description,
            date_time: formData.date,
            location: formData.location,
            category: formData.category,
            tone: formData.tone,
            max_guests: formData.maxApplicants ? parseInt(formData.maxApplicants) : undefined,
            host_name: currentUser?.fullName || currentUser?.username || '',
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate description');
      }

      if (result.success && result.description) {
        setFormData(prev => ({ ...prev, description: result.description }));
        if (errors.description) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.description;
            return newErrors;
          });
        }
      }
    } catch (error: any) {
      console.error('Error generating description:', error);
      setDescriptionError('We couldn\'t enhance this just now. Try again, or write your own description.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const validateStep = (currentStep: number): boolean => {
    const newErrors: Record<string, string> = {};
    switch (currentStep) {
      case 1:
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        // Check for inappropriate content - MUST be clean before submission
        if (containsProfanity(formData.title)) {
          newErrors.title = 'Please remove inappropriate language from the title.';
        }
        if (containsProfanity(formData.description)) {
          newErrors.description = 'Please remove inappropriate language from the description.';
        }
        break;
      case 2:
        if (!formData.location.trim()) newErrors.location = 'Location is required';
        if (!formData.date || formData.date.includes('00')) newErrors.date = 'Date is required';
        if (!formData.category) newErrors.category = 'Category is required';
        if (formData.maxApplicants && (isNaN(Number(formData.maxApplicants)) || Number(formData.maxApplicants) <= 0)) {
          newErrors.maxApplicants = 'Maximum applicants must be a positive number';
        }
        if (formData.maxApplicants && Number(formData.maxApplicants) > 10) {
          newErrors.maxApplicants = 'Maximum applicants cannot exceed 10';
        }
        break;
      case 3:
        if (formData.minAge && (isNaN(Number(formData.minAge)) || Number(formData.minAge) < 18)) {
          newErrors.minAge = 'Minimum age must be at least 18';
        }
        if (
          formData.maxAge &&
          (isNaN(Number(formData.maxAge)) || Number(formData.maxAge) < Number(formData.minAge || 18))
        ) {
          newErrors.maxAge = 'Maximum age must be greater than minimum age';
        }
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      // Track step completion
      if (currentUser) {
        trackCreateVybeStepCompleted({
          user_id: currentUser.id,
          step_name: step === 1 ? 'basics' : step === 2 ? 'details' : 'preferences',
          step_number: step,
          category: formData.category ? standardizeCategory(formData.category) : undefined,
        });
        // Track next step start
        trackCreateVybeStepStarted({
          user_id: currentUser.id,
          step_name: step === 1 ? 'details' : step === 2 ? 'preferences' : 'review',
          step_number: step + 1,
          category: formData.category ? standardizeCategory(formData.category) : undefined,
        });
      }
      setStep(step + 1);
    }
  };

  const handleBack = () => setStep(step - 1);
  const handleCancel = () => setShowCancelConfirm(true);
  const handleConfirmCancel = () => {
    // Reset form completely when cancelling
    setStep(1);
    setFormData({
      title: '',
      description: '',
      location: '',
      date: '',
      category: '',
      maxApplicants: '10',
      preferredGender: 'prefer-not-to-say',
      minAge: '',
      maxAge: '',
      preferredInterests: [],
      visibility: 'public',
      tone: 'friendly',
      customImage: null,
      customImageUrl: ''
    });
    setErrors({});
    setSuccess(false);
    setShowCancelConfirm(false);
    onClose();
  };

  const handleSaveAsDraft = async () => {
    if (!currentUser) return;

    // Check if user has create tokens using the new token system
    const isSubscribed = currentUser.subscriptionTier === 'subscribed';
    const { canCreate, reason } = await canCreateVybe(currentUser.id, isSubscribed);

    if (!canCreate) {
      setErrors({
        submit: reason || 'Unable to create Vybe at this time'
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      const eventData = {
        // only safe fields—no personal contact info
        title: formData.title,
        description: formData.description,
        location: formData.location,
        date: formData.date,
        category: formData.category,
        latitude: formData.latitude,
        longitude: formData.longitude,
        posterId: currentUser.id,
        maxApplicants: Number(formData.maxApplicants),
        preferredDemographics: {
          gender: formData.preferredGender,
          minAge: formData.minAge ? Number(formData.minAge) : undefined,
          maxAge: formData.maxAge ? Number(formData.maxAge) : undefined,
          interests: formData.preferredInterests.length > 0 ? formData.preferredInterests : undefined
        },
        visibility: formData.visibility,
        status: 'draft' as const
      };
      const newEvent = await createEvent(eventData);
      setSuccess(true);
      setCreatedEventId(newEvent.id);
      setShowVybeCreated(true);
    } catch (error) {
      console.error('Failed to save draft:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save draft. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    console.log('handleSubmit called, step:', step, 'currentUser:', !!currentUser);

    // This should only be called on step 3
    if (step !== 3) {
      console.warn('handleSubmit called on wrong step:', step);
      return;
    }

    if (!validateStep(step)) {
      console.error('Validation failed on step 3');
      return;
    }

    if (!currentUser) {
      console.error('No current user');
      setErrors({ submit: 'You must be logged in to create a Vybe' });
      return;
    }

    // Verification is optional - no blocking checks needed

    const isSubscribed = currentUser.subscriptionTier === 'subscribed';
    const isAdmin = currentUser.isAdmin === true;
    console.log('Checking Vybe limits:', { isSubscribed, isAdmin });

    setIsSubmitting(true);
    setErrors({});
    try {
      // Track vybe creation start
      trackEvent('vybe_create_started', {
        category: formData.category,
        user_id: currentUser.id
      }, currentUser.id);

      // Admin users skip token consumption
      if (!isAdmin) {
        // Generate idempotency key for this operation to prevent double-consumption on retry
        const idempotencyKey = `${currentUser.id}_create_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Atomically check limits and consume token BEFORE creating event
        // Validation has already occurred above, so token will only be consumed for valid data
        // This wrapper automatically refreshes user state on success
        const consumeResult = await consumeVybeCreateLimitWithRefresh(
          currentUser.id,
          isSubscribed,
          refreshCurrentUser,
          idempotencyKey
        );

        if (!consumeResult.success) {
          setLimitErrorMessage(consumeResult.reason || 'Unable to create Vybe at this time');
          setShowTokenModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      // Upload custom image if provided (admin only)
      let uploadedImageUrl: string | undefined = undefined;
      if (isAdmin) {
        // Priority 1: Use URL if provided
        if (formData.customImageUrl?.trim()) {
          uploadedImageUrl = formData.customImageUrl.trim();
        }
        // Priority 2: Upload file if provided
        else if (formData.customImage) {
          try {
            const fileExt = formData.customImage.name.split('.').pop();
            const fileName = `${currentUser.id}_${Date.now()}.${fileExt}`;
            const filePath = `vybe-images/${fileName}`;

            const { error: uploadError } = await supabase.storage
              .from('vybe-images')
              .upload(filePath, formData.customImage, {
                cacheControl: '3600',
                upsert: false
              });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('vybe-images')
              .getPublicUrl(filePath);

            uploadedImageUrl = publicUrl;
          } catch (uploadError) {
            console.error('Failed to upload custom image:', uploadError);
            setErrors({ submit: 'Failed to upload image. Please try again.' });
            setIsSubmitting(false);
            return;
          }
        }
      }

      const eventData = {
        // only safe fields—no personal contact info
        title: formData.title,
        description: formData.description,
        location: formData.location,
        date: formData.date,
        category: formData.category,
        latitude: formData.latitude,
        longitude: formData.longitude,
        posterId: currentUser.id,
        imageUrl: uploadedImageUrl,
        sourceType: 'user_created',
        systemHost: isAdmin, // Admin-created Vybes are community Vybes
        maxApplicants: Number(formData.maxApplicants),
        preferredDemographics: {
          gender: formData.preferredGender,
          minAge: formData.minAge ? Number(formData.minAge) : undefined,
          maxAge: formData.maxAge ? Number(formData.maxAge) : undefined,
          interests: formData.preferredInterests
        },
        visibility: formData.visibility,
        status: 'published' as const,
        isBotCreated: formData.isCommunityVybe || false,
        autoAcceptEnabled: formData.isCommunityVybe || false
      };
      // Track publish click
      trackCreateVybePublishClicked({
        user_id: currentUser.id,
        category: standardizeCategory(formData.category),
      });

      const newEvent = await createEvent(eventData);

      // Extract location area (e.g., first part of postcode or city)
      const locationArea = formData.location.split(',')[0].trim();

      // Track successful Vybe creation
      trackVybeCreated({
        vybe_id: newEvent.id,
        user_id: currentUser.id,
        category: standardizeCategory(formData.category),
        location_area: locationArea,
        is_free: true, // All Vybes are free for now
      });

      trackCreateVybeStepCompleted({
        user_id: currentUser.id,
        step_name: 'preferences',
        step_number: 3,
        category: standardizeCategory(formData.category),
      });

      // Track successful vybe creation
      trackEvent('vybe_create_completed', {
        vybe_id: newEvent.id,
        category: formData.category,
        user_id: currentUser.id
      }, currentUser.id);

      // Track vybe_created for B2B analytics
      trackVybeCreatedB2B(newEvent.id, currentUser.id, formData.category, locationArea);

      console.log('[CreateEventModal] Event created successfully with ID:', newEvent.id);
      setSuccess(true);
      setCreatedEventId(newEvent.id);
      setShowVybeCreated(true);
    } catch (error) {
      console.error('Failed to create event:', error);

      // Track vybe creation failure
      trackEvent('vybe_create_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        category: formData.category,
        user_id: currentUser.id
      }, currentUser.id);

      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to create event. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (showCancelConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-primary-50 rounded-lg shadow-elegant p-6 max-w-md w-full border border-primary-200"
        >
          <h3 className="text-xl font-semibold mb-4 text-contrast">Cancel Event Creation?</h3>
          <p className="text-contrast-400 mb-6">Are you sure you want to cancel? All your progress will be lost.</p>
          <div className="flex justify-end space-x-4">
            <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-contrast hover:text-contrast-600">
              Continue Editing
            </button>
            <button onClick={handleConfirmCancel} className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-4 py-2.5 rounded-full transition-colors font-semibold shadow-lg">
              Yes, Cancel
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4 overflow-y-auto">
      <div className="bg-theme-cards rounded-none sm:rounded-2xl shadow-card border-0 sm:border border-theme-cards w-full max-w-4xl min-h-screen sm:min-h-0 sm:my-4 sm:my-8">
        <div className="p-3 sm:p-4 border-b border-primary-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold text-contrast">Start a New Vybe</h2>
          <button
            onClick={handleCancel}
            className="text-contrast-500 hover:text-contrast p-2 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 max-h-[calc(100vh-8rem)] sm:max-h-[80vh] overflow-y-auto">
          {success ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4 sm:py-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <Check size={32} className="text-accent2" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-contrast">Vybe Started Successfully!</h2>
              <p className="text-contrast-400 mb-3 sm:mb-4 text-sm sm:text-base">
                Your Vybe has been started successfully. You can manage your Vybes and view requests from the "My Vybes"
                page.
              </p>
            </motion.div>
          ) : (
            <>
              {/* Progress Steps */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map(stepNumber => (
                    <div key={stepNumber} className="flex items-center">
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          stepNumber === step
                            ? 'bg-secondary text-white'
                            : stepNumber < step
                            ? 'bg-accent2 text-primary'
                            : 'bg-primary-200 text-contrast-600'
                        }`}
                      >
                        {stepNumber < step ? <Check size={16} className="sm:size-20" /> : stepNumber}
                      </div>
                      <div
                        className={`ml-1 sm:ml-2 text-xs sm:text-sm md:text-base ${
                          stepNumber === step ? 'font-medium text-contrast' : 'text-contrast-500'
                        }`}
                      >
                        <span className="hidden sm:inline">{stepNumber === 1 ? 'Basic Info' : stepNumber === 2 ? 'Event Details' : 'Preferences'}</span>
                        <span className="sm:hidden">{stepNumber === 1 ? 'Info' : stepNumber === 2 ? 'Details' : 'Prefs'}</span>
                      </div>
                      {stepNumber < 3 && <div className="w-2 sm:w-12 md:w-24 border-t border-primary-300 mx-1 sm:mx-2"></div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Content */}
              {errors.submit && (
                <div className="bg-secondary/10 text-secondary p-4 rounded-md mb-6 flex items-start">
                  <Info size={20} className="mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Error</p>
                    <p>{errors.submit}</p>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => {
                e.preventDefault();
                // Only allow form submission on step 3
                if (step === 3) {
                  handleSubmit(e);
                } else if (step < 3) {
                  // On steps 1 and 2, treat Enter as clicking Next
                  handleNext();
                }
              }}>
                <AnimatePresence mode="wait">
                  {/* Step 1: Basic Info */}
                  {step === 1 && (
                    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Basic Information</h2>

                      <div className="mb-6">
                        <label htmlFor="title" className="block text-sm font-medium text-contrast mb-1">
                          Vybe Title*
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          className={`w-full p-3 text-base bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all ${
                            errors.title ? 'border-secondary' : 'border-blue-400/30 hover:border-blue-400/50'
                          }`}
                          placeholder="Give your Vybe a clear, descriptive title"
                        />
                        {errors.title && <p className="text-secondary text-sm mt-1">{errors.title}</p>}
                      </div>

                      <div className="mb-6">
                        <label htmlFor="description" className="block text-sm font-medium text-contrast mb-1">
                          Description*
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={6}
                          enterKeyHint="enter"
                          className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all ${
                            errors.description ? 'border-secondary' : 'border-blue-400/30 hover:border-blue-400/50'
                          }`}
                          placeholder="Describe your Vybe in detail. What will participants do? What should they expect? Why should they join?"
                          disabled={isGeneratingDescription}
                        />
                        {errors.description && <p className="text-secondary text-sm mt-1">{errors.description}</p>}

                        <div className="flex items-center justify-between mt-2 gap-3">
                          <p className="text-sm text-contrast-500">Press Enter or Return to add new lines. Be specific and include any important details.</p>

                          <button
                            type="button"
                            onClick={handleEnhanceWithAI}
                            disabled={isGeneratingDescription || !formData.title.trim()}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all shrink-0 ${
                              isGeneratingDescription || !formData.title.trim()
                                ? 'bg-primary-200 text-contrast-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-contrast hover:from-purple-500/30 hover:to-pink-500/30'
                            }`}
                          >
                            {isGeneratingDescription ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-contrast border-t-transparent" />
                                <span className="text-sm">Enhancing...</span>
                              </>
                            ) : (
                              <>
                                <Wand2 className="w-4 h-4" />
                                <span className="text-sm">Enhance with AI</span>
                              </>
                            )}
                          </button>
                        </div>

                        {descriptionError && (
                          <p className="text-secondary text-sm mt-2 bg-secondary/10 border border-secondary/20 rounded-lg p-2">
                            {descriptionError}
                          </p>
                        )}
                      </div>

                      {/* Admin Image Upload (only for admin users) */}
                      {currentUser?.isAdmin && (
                        <div className="mb-6 space-y-4">
                          <div>
                            <label htmlFor="customImageUrl" className="block text-sm font-medium text-contrast mb-1">
                              Image URL (Recommended)
                            </label>
                            <input
                              type="text"
                              id="customImageUrl"
                              name="customImageUrl"
                              value={formData.customImageUrl || ''}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, customImageUrl: e.target.value, customImage: null }));
                                if (errors.customImageUrl) {
                                  const newErrors = { ...errors };
                                  delete newErrors.customImageUrl;
                                  setErrors(newErrors);
                                }
                              }}
                              placeholder="https://images.unsplash.com/..."
                              className="w-full p-3 bg-primary-100 border border-blue-400/30 hover:border-blue-400/50 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all text-contrast"
                            />
                            {formData.customImageUrl && (
                              <div className="text-sm text-accent2 flex items-center gap-2 mt-2">
                                <Check size={16} />
                                <span>URL added</span>
                              </div>
                            )}
                            {errors.customImageUrl && <p className="text-secondary text-sm mt-1">{errors.customImageUrl}</p>}
                            <p className="text-xs text-contrast-500 mt-1">
                              Paste an Unsplash image URL for this Community Vybe. This is the easiest option.
                            </p>
                          </div>

                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-primary-200"></div>
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-primary px-2 text-contrast-500">Or</span>
                            </div>
                          </div>

                          <div>
                            <label htmlFor="customImage" className="block text-sm font-medium text-contrast mb-1">
                              Upload Image File
                            </label>
                            <input
                              type="file"
                              id="customImage"
                              name="customImage"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="w-full p-3 bg-primary-100 border border-blue-400/30 hover:border-blue-400/50 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all"
                            />
                            {formData.customImage && (
                              <div className="text-sm text-accent2 flex items-center gap-2 mt-2">
                                <Check size={16} />
                                <span>Image selected: {formData.customImage.name}</span>
                              </div>
                            )}
                            {errors.customImage && <p className="text-secondary text-sm mt-1">{errors.customImage}</p>}
                            <p className="text-xs text-contrast-500 mt-1">
                              Max size: 5MB. If no image is provided above, an AI image will be generated.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* AI Image Explanation */}
                      {!currentUser?.isAdmin && (
                        <div className="mb-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm text-contrast leading-relaxed">
                                <strong>Your Vybe image will be generated automatically</strong> based on your title and category. This keeps the focus on the experience, not on perfect photos.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tone Selector */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-contrast mb-2">
                          Vybe Tone*
                        </label>
                        <p className="text-xs text-contrast-500 mb-3">
                          Choose the tone for your Vybe. This helps the AI understand the vibe you're going for.
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {[
                            { value: 'outgoing', label: '🎉 Outgoing & Social', desc: 'Expressive and chatty' },
                            { value: 'calm', label: '🧘 Calm & Chill', desc: 'Relaxed and easygoing' },
                            { value: 'energetic', label: '🔥 High-Energy', desc: 'Punchy and upbeat' },
                            { value: 'creative', label: '🎨 Creative & Curious', desc: 'Thoughtful and artsy' },
                            { value: 'friendly', label: '🤝 Friendly & Open', desc: 'Warm and welcoming' },
                            { value: 'adventurous', label: '🏞️ Adventurous', desc: 'Bold and exciting' }
                          ].map((tone) => (
                            <button
                              key={tone.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, tone: tone.value }))}
                              className={`p-3 rounded-lg border-2 transition-all text-left ${
                                formData.tone === tone.value
                                  ? 'border-secondary bg-secondary/10'
                                  : 'border-primary-200 hover:border-primary-300 bg-primary-50'
                              }`}
                            >
                              <div className="text-sm font-medium text-contrast mb-1">{tone.label}</div>
                              <div className="text-xs text-contrast-400">{tone.desc}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Event Details */}
                  {step === 2 && (
                    <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Vybe Details</h2>

                      <div className="mb-6">
                        <label htmlFor="location" className="block text-sm font-medium text-contrast mb-1">
                          Location*
                        </label>
                        <LocationSearch onLocationSelect={handleLocationSelect} placeholder="Enter Vybe location" initialLocation={formData.location} />
                        {errors.location && <p className="text-secondary text-sm mt-1">{errors.location}</p>}
                        <div className="mt-2 flex items-start gap-2 text-xs text-theme-text-secondary bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-400" />
                          <p>
                            Everyone sees an approximate area on the map. Share the exact meetup point with your Tribe in the Vybe chat when ready.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="pt-2">
                          <label htmlFor="date" className="block text-sm font-medium text-contrast mb-2">
                            Date*
                          </label>
                          <DatePicker selectedDate={formData.date} onChange={handleDateChange} minDate={new Date().toISOString().split('T')[0]} mode="future" />
                          {errors.date && <p className="text-secondary text-sm mt-1">{errors.date}</p>}
                        </div>

                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-contrast mb-1">
                            Category*
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Tag size={18} className="text-contrast-500" />
                            </div>
                            <select
                              id="category"
                              name="category"
                              value={formData.category}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleChange(e);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`w-full pl-10 p-3 bg-primary-100 border-2 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary appearance-none transition-all ${
                                errors.category ? 'border-secondary' : 'border-blue-400/50 hover:border-blue-400/70'
                              }`}
                              style={{
                                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                backgroundPosition: 'right 0.5rem center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: '1.5em 1.5em',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="">Select a category</option>
                              {categories.map(category => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </select>
                          </div>
                          {errors.category && <p className="text-secondary text-sm mt-1">{errors.category}</p>}
                        </div>
                      </div>

                      <div className="mb-6">
                        <label htmlFor="maxApplicants" className="block text-sm font-medium text-contrast mb-1">
                          Maximum Tribe Members
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Users size={18} className="text-contrast-500" />
                          </div>
                          <input
                            type="number"
                            id="maxApplicants"
                            name="maxApplicants"
                            value={formData.maxApplicants}
                            onChange={handleChange}
                            min="1"
                            max="10"
                            required
                            className={`w-full pl-10 p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all ${
                              errors.maxApplicants ? 'border-secondary' : 'border-blue-400/30 hover:border-blue-400/50'
                            }`}
                            placeholder="Enter number (max 10)"
                          />
                        </div>
                        {errors.maxApplicants && <p className="text-secondary text-sm mt-1">{errors.maxApplicants}</p>}
                        <p className="text-sm text-contrast-500 mt-1">
                          Maximum 10 participants.
                        </p>
                      </div>

                    </motion.div>
                  )}

                  {/* Step 3: Preferences */}
                  {step === 3 && (
                    <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h2 className="text-xl font-bold mb-6 text-contrast">Preferences</h2>

                      <div className="mb-6">
                        <h3 className="text-lg font-medium mb-3 text-contrast">Vybe Visibility</h3>
                        <p className="text-contrast-400 mb-4">
                          Choose who can see and apply to your Vybe.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, visibility: 'public' }))}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${
                              formData.visibility === 'public'
                                ? 'border-secondary bg-secondary/10'
                                : 'border-primary-200 hover:border-primary-300 bg-primary-50'
                            }`}
                          >
                            <div className="flex items-start">
                              <Users size={24} className={`mr-3 mt-0.5 ${
                                formData.visibility === 'public' ? 'text-secondary' : 'text-contrast-500'
                              }`} />
                              <div>
                                <h4 className="font-semibold text-contrast mb-1">All Users</h4>
                                <p className="text-sm text-contrast-400">Everyone can see and apply to this Vybe</p>
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, visibility: 'tribe_only' }))}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${
                              formData.visibility === 'tribe_only'
                                ? 'border-secondary bg-secondary/10'
                                : 'border-primary-200 hover:border-primary-300 bg-primary-50'
                            }`}
                          >
                            <div className="flex items-start">
                              <Users size={24} className={`mr-3 mt-0.5 ${
                                formData.visibility === 'tribe_only' ? 'text-secondary' : 'text-contrast-500'
                              }`} />
                              <div>
                                <h4 className="font-semibold text-contrast mb-1">Tribe Only</h4>
                                <p className="text-sm text-contrast-400">Only your connections can see this Vybe</p>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-medium mb-3 text-contrast">Tribe Member Preferences</h3>
                        <p className="text-contrast-400 mb-4">
                          These preferences help match your Vybe with suitable Tribe Members. They are not strict requirements.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          <div>
                            <label htmlFor="preferredGender" className="block text-sm font-medium text-contrast mb-1">
                              Gender Preference
                            </label>
                            <select
                              id="preferredGender"
                              name="preferredGender"
                              value={formData.preferredGender}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleChange(e);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full p-3 bg-primary-100 border-2 border-blue-400/50 hover:border-blue-400/70 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast transition-all appearance-none"
                              style={{
                                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                backgroundPosition: 'right 0.5rem center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: '1.5em 1.5em',
                                paddingRight: '2.5rem'
                              }}
                            >
                              <option value="prefer-not-to-say">Any Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="non-binary">Non-binary/Other</option>
                            </select>
                          </div>

                          <div>
                            <label htmlFor="minAge" className="block text-sm font-medium text-contrast mb-1">
                              Minimum Age
                            </label>
                            <input
                              type="number"
                              id="minAge"
                              name="minAge"
                              value={formData.minAge}
                              onChange={handleChange}
                              min="18"
                              className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all ${
                                errors.minAge ? 'border-secondary' : 'border-blue-400/30 hover:border-blue-400/50'
                              }`}
                              placeholder="e.g. 18"
                            />
                            {errors.minAge && <p className="text-secondary text-sm mt-1">{errors.minAge}</p>}
                          </div>

                          <div>
                            <label htmlFor="maxAge" className="block text-sm font-medium text-contrast mb-1">
                              Maximum Age
                            </label>
                            <input
                              type="number"
                              id="maxAge"
                              name="maxAge"
                              value={formData.maxAge}
                              onChange={handleChange}
                              min={formData.minAge || '18'}
                              className={`w-full p-3 bg-primary-100 border rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary transition-all ${
                                errors.maxAge ? 'border-secondary' : 'border-blue-400/30 hover:border-blue-400/50'
                              }`}
                              placeholder="e.g. 65"
                            />
                            {errors.maxAge && <p className="text-secondary text-sm mt-1">{errors.maxAge}</p>}
                          </div>
                        </div>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-contrast mb-2">Preferred Interests</label>
                          <InterestSelector selectedInterests={formData.preferredInterests} onChange={handleInterestChange} />
                          <p className="text-sm text-contrast-500 mt-2">
                            Select interests that are relevant to your Vybe. This helps match your Vybe with interested Tribe Members.
                          </p>
                        </div>

                        {/* Admin Community Vybe Toggle */}
                        {currentUser?.isAdmin && (
                          <div className="p-4 bg-accent2/10 border border-accent2/20 rounded-lg">
                            <label className="flex items-start cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.isCommunityVybe || false}
                                onChange={(e) => setFormData(prev => ({ ...prev, isCommunityVybe: e.target.checked }))}
                                className="mt-1 w-5 h-5 text-accent2 bg-primary-100 border-accent2/50 rounded focus:ring-accent2 focus:ring-offset-0"
                              />
                              <div className="ml-3">
                                <span className="font-semibold text-contrast">Create as Community Vybe</span>
                                <p className="text-sm text-contrast-400 mt-1">
                                  Auto-accept applications until capacity is reached. Applications won't require manual review.
                                </p>
                              </div>
                            </label>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="mt-6 border-t border-primary-200 pt-4">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="flex items-center justify-center sm:justify-start px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all group min-h-[48px]"
                      >
                        <ChevronLeft size={20} className="mr-2 group-hover:-translate-x-0.5 transition-transform" />
                        <span className="font-medium">Back</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="flex items-center justify-center sm:justify-start px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all group min-h-[48px]"
                      >
                        <X size={20} className="mr-2" />
                        <span className="font-medium">Cancel</span>
                      </button>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 sm:flex-1 sm:justify-end">
                      {step === 3 && (
                        <button
                          type="button"
                          onClick={handleSaveAsDraft}
                          disabled={isSubmitting}
                          className="flex items-center justify-center px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed font-semibold border border-white/20 hover:border-white/30 hover:shadow-lg group min-h-[48px]"
                        >
                          <span>Save as Draft</span>
                        </button>
                      )}

                      {step < 3 ? (
                        <button
                          type="button"
                          onClick={handleNext}
                          className="flex items-center justify-center bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-8 py-3 rounded-xl transition-all font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] group min-h-[48px]"
                        >
                          <span>Next</span>
                          <ChevronRight size={20} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="flex items-center justify-center bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-8 py-3 rounded-xl transition-all font-bold shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] relative overflow-hidden min-h-[48px]"
                        >
                          <span className="relative z-10">{isSubmitting ? 'Starting...' : 'Start Vybe'}</span>
                          {!isSubmitting && <Check size={20} className="ml-2 relative z-10" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      <VybeCreatedModal
        isOpen={showVybeCreated}
        onClose={() => {
          setShowVybeCreated(false);
          onEventCreated();
          onClose();
        }}
        eventTitle={formData.title}
        eventId={createdEventId}
      />
      <TokenTopUpModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        reason={limitErrorMessage}
      />
    </div>
  );
};

export default React.memo(CreateEventModal);
